import React, { useEffect, useRef, useState, useMemo } from 'react';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useUser } from '../context/UserContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Slider,
  Divider,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { ChromePicker } from 'react-color';
import { getChatbotConfig, saveChatbotConfig } from '../services/api';

const Settings = () => {
  const { user } = useUser();
  const isReadOnly = user?.rol === 'readonly';
  const iframeRef = useRef(null);
  const { selectedChatbot } = useSelectedChatbot();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Definición de temas disponibles
  const defaultThemes = [
    // Tema claro (índice 0)
    {
      chatBackground: '#ffffff',
      chatBorder: '#e0e0e0',
      userMessageBg: '#0b93f6',
      userMessageBg2: '#00c6ff', // Color secundario para el degradado
      userMessageText: '#ffffff',
      assistantMessageBg: '#f5f5f5',
      assistantMessageBg2: '#e9e9e9', // Color secundario para el degradado
      assistantMessageText: '#000000',
      assistantMessageBorder: '#ff8c00',
      inputBorder: '#0b93f6',
      inputFocusBorder: '#0056b3', // Color del borde cuando el campo tiene focus
      inputBackground: '#ffffff',
      inputText: '#333333',
      placeholderColor: '#666666',
      headerBackground: '#ffffff',
      headerBorder: '#ddd',
      headerText: '#333333',
      scrollbarThumb: '#999999',
      scrollbarTrack: '#f1f1f1',
      linkColor: '#007bff',
      linkHoverColor: '#0056b3',
      statusColor: '#4caf50'
    },

    // Tema oscuro (índice 1)
    {
      chatBackground: '#1a1a1a',
      chatBorder: '#2d2d2d',
      userMessageBg: '#0d47a1',
      userMessageBg2: '#1976d2', // Color secundario para el degradado
      userMessageText: '#ffffff',
      assistantMessageBg: '#2d2d2d',
      assistantMessageBg2: '#3d3d3d', // Color secundario para el degradado
      assistantMessageText: '#ffffff',
      assistantMessageBorder: '#ff8c00',
      inputBorder: '#0d47a1',
      inputFocusBorder: '#0056b3', // Color del borde cuando el campo tiene focus
      inputBackground: '#2d2d2d',
      inputText: '#ffffff',
      placeholderColor: '#999999',
      headerBackground: '#2d2d2d',
      headerBorder: '#3d3d3d',
      headerText: '#ffffff',
      scrollbarThumb: '#666666',
      scrollbarTrack: '#2d2d2d',
      linkColor: '#4fc3f7',
      linkHoverColor: '#81d4fa',
      statusColor: '#4caf50'
    }
  ]

  // Estado inicial con placeholders por defecto
  const defaultConfig = {
    theme: {
      current: 0,
      themes: defaultThemes
    },
    TITULO_NOM: 'Asistente IA AI4Assistant',
    PRIMER_MENSAJE: 'Hola, soy AI4Assistant, asistente de la plataforma, ¿En qué puedo ayudarte?',
    CLIENTE_EMAIL: 'tuemail@ai4logic.com',
    CLIENTE_EMAIL_NOM: 'Tu nombre',
    CLIENTE_WEB: 'https://tuweb.com/',
    NOMBRE: 'AI4Logic web',
    TITULO_ICO: 'logo1',
    TITULO_ICO_URL: '',
    STYLE_sizebutton: 60,
    STYLE_animation: true,
    STYLE_openanimation: true,
    STYLE_firstrunopen: false,
    STYLE_autoopen_delay: "0",
    STYLE_position_marginX: 20,
    STYLE_position_marginY: 20,
    VISTA_modo: 'claro',
    VISTA_buttoncolor1: '#ff0080',
    VISTA_buttoncolor2: '#ff8c00',
    FRASES_enabled: false,
    FRASES_1: '',
    FRASES_2: '',
    FRASES_3: '',
    FRASES_4: '',
    FRASES_5: '',
    // Powered by (solo visible si el admin lo habilita en AdminOptions)
    POWERED_BY_enabled: true,
    // Texto "Powered by" (label)
    POWERED_BY_label: 'Powered by',
    // Texto del enlace (nombre de la marca)
    POWERED_BY_linkText: 'AI4Logic',
    POWERED_BY_url: 'https://ai4logic.com',
    // Colores
    POWERED_BY_linkColor: '#0b93f6',
    // labelColor depende del modo: blanco para oscuro, negro para claro
    POWERED_BY_labelColor: '#000000' // Por defecto modo claro
  };

  // Helper: obtener color de label por defecto según el modo
  const getDefaultLabelColor = (modo) => modo === 'oscuro' ? '#ffffff' : '#000000';

  const [pendingConfig, setPendingConfig] = useState(defaultConfig);
  const [savedConfig, setSavedConfig] = useState(null);
  const [fullConfig, setFullConfig] = useState(null); // Guardar el objeto original completo para evitar pérdida de datos
  const [isLoading, setIsLoading] = useState(true);
  const [colorAnchor1, setColorAnchor1] = useState(null);
  const [colorAnchor2, setColorAnchor2] = useState(null);
  const [colorCustomizationOpen, setColorCustomizationOpen] = useState(false);
  const [iconMenuAnchor, setIconMenuAnchor] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now()); // Para forzar recarga del iframe
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Detectar si este chatbot permite Powered By modificable (CUSTOM_POWERED_BY=1 en opciones)
  const hasCustomPoweredBy = useMemo(() => {
    if (!user || !selectedChatbot || !user.products) return false;
    const prod = user.products.find(p => p.code === selectedChatbot);
    if (!prod || !prod.options) return false;
    const match = prod.options.match(/CUSTOM_POWERED_BY=(\d+|yes)/i);
    if (!match) return false;
    const val = match[1].toString().toLowerCase();
    return val === '1' || val === 'yes';
  }, [user, selectedChatbot]);

  // Cargar datos del chatbot al iniciar
  useEffect(() => {
    const fetchChatbotData = async () => {
      if (!selectedChatbot) return;

      try {
        const data = await getChatbotConfig(selectedChatbot);
        setFullConfig(data); // Guardamos la copia original intacta

        if (data) {
          // Convertir la estructura de la API a nuestro formato
          const configFromApi = {
            TITULO_NOM: data.title_name || defaultConfig.TITULO_NOM,
            PRIMER_MENSAJE: data.welcome || defaultConfig.PRIMER_MENSAJE,
            CLIENTE_EMAIL: data.client_email || defaultConfig.CLIENTE_EMAIL,
            CLIENTE_EMAIL_NOM: data.client_emailname || defaultConfig.CLIENTE_EMAIL_NOM,
            CLIENTE_WEB: data.client_web || defaultConfig.CLIENTE_WEB,
            NOMBRE: data.name || defaultConfig.NOMBRE,
            TITULO_ICO: data.title_icon ? 'custom' : 'logo1',
            TITULO_ICO_URL: data.title_icon || '',
            STYLE_sizebutton: parseInt(data.styles?.sizebutton) || defaultConfig.STYLE_sizebutton,
            STYLE_animation: data.styles?.animation === 'true' || defaultConfig.STYLE_animation,
            STYLE_openanimation: data.styles?.openanimation === 'true' || defaultConfig.STYLE_openanimation,
            STYLE_firstrunopen: data.styles?.firstrunopen === 'true' || defaultConfig.STYLE_firstrunopen,
            STYLE_autoopen_delay: data.styles?.autoopen_delay || defaultConfig.STYLE_autoopen_delay,
            STYLE_position_marginX: parseInt(data.styles?.position_marginX) || defaultConfig.STYLE_position_marginX,
            STYLE_position_marginY: parseInt(data.styles?.position_marginY) || defaultConfig.STYLE_position_marginY,
            theme: data.theme || defaultConfig.theme,
            VISTA_buttoncolor1: data.styles?.buttoncolor1 || defaultConfig.VISTA_buttoncolor1,
            VISTA_buttoncolor2: data.styles?.buttoncolor2 || defaultConfig.VISTA_buttoncolor2,
            FRASES_enabled: data.frases ? true : defaultConfig.FRASES_enabled,
            FRASES_1: data.frases?.['1'] || defaultConfig.FRASES_1,
            FRASES_2: data.frases?.['2'] || defaultConfig.FRASES_2,
            FRASES_3: data.frases?.['3'] || defaultConfig.FRASES_3,
            FRASES_4: data.frases?.['4'] || defaultConfig.FRASES_4,
            FRASES_5: data.frases?.['5'] || defaultConfig.FRASES_5,
            // Powered by (si la API lo devuelve)
            POWERED_BY_enabled: data.powered_by?.enabled ?? defaultConfig.POWERED_BY_enabled,
            POWERED_BY_label: data.powered_by?.label || defaultConfig.POWERED_BY_label,
            // Compatibilidad: si no viene linkText, usar text antiguo
            POWERED_BY_linkText: data.powered_by?.linkText || data.powered_by?.text || defaultConfig.POWERED_BY_linkText,
            POWERED_BY_url: data.powered_by?.url || defaultConfig.POWERED_BY_url,
            POWERED_BY_linkColor: data.powered_by?.linkColor || defaultConfig.POWERED_BY_linkColor,
            // Si no hay labelColor guardado, usar color según el modo actual (0=claro, 1=oscuro)
            POWERED_BY_labelColor: data.powered_by?.labelColor || getDefaultLabelColor((data.theme?.current === 1) ? 'oscuro' : 'claro')
          };

          // Manejar la nueva estructura de temas
          if (data.theme?.themes) {
            // Asegurarse de que el tema actual esté dentro de los límites
            const currentThemeIndex = data.theme.current || 0;
            const currentTheme = data.theme.themes[currentThemeIndex];

            if (currentTheme) {
              // Aplicar los colores del tema actual como colores personalizados
              Object.entries(currentTheme).forEach(([key, value]) => {
                configFromApi[`CUSTOM_${key}`] = value;
              });
            }
          }
          // Mantener compatibilidad con la estructura antigua
          else if (data.colorespersonalizados) {
            Object.keys(data.colorespersonalizados).forEach(key => {
              configFromApi[`CUSTOM_${key}`] = data.colorespersonalizados[key];
            });
          }

          setPendingConfig(configFromApi);
          setSavedConfig(configFromApi);
        } else {
          // Si no hay datos, usar los valores por defecto
          setFullConfig({});
          setPendingConfig(defaultConfig);
          setSavedConfig(defaultConfig);
        }
      } catch (error) {
        console.error('Error al obtener datos del chatbot:', error);
        // En caso de error, usar los valores por defecto
        setPendingConfig(defaultConfig);
        setSavedConfig(defaultConfig);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatbotData();
  }, [selectedChatbot]);

  const handleChange = (field, value) => {
    setPendingConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    if (!selectedChatbot) return;

    try {
      // Convertir nuestro formato al formato de la API, fusionando con el objeto original
      const configForApi = {
        ...fullConfig, // Preservar todos los campos que no gestionamos (ej. notifications)
        title_name: pendingConfig.TITULO_NOM,
        welcome: pendingConfig.PRIMER_MENSAJE,
        client_email: pendingConfig.CLIENTE_EMAIL,
        client_emailname: pendingConfig.CLIENTE_EMAIL_NOM,
        client_web: pendingConfig.CLIENTE_WEB,
        name: pendingConfig.NOMBRE,
        title_icon: pendingConfig.TITULO_ICO === 'custom'
          ? pendingConfig.TITULO_ICO_URL
          : `https://ai4logic.com/${pendingConfig.TITULO_ICO}.png`,
        theme: {
          ...(fullConfig?.theme || {}),
          current: pendingConfig.theme?.current ?? 0,
          themes: pendingConfig.theme?.themes ? JSON.parse(JSON.stringify(pendingConfig.theme.themes)) : JSON.parse(JSON.stringify(defaultThemes))
        },
        styles: {
          ...(fullConfig?.styles || {}),
          sizebutton: String(pendingConfig.STYLE_sizebutton),
          animation: String(pendingConfig.STYLE_animation),
          openanimation: String(pendingConfig.STYLE_openanimation),
          firstrunopen: String(pendingConfig.STYLE_firstrunopen),
          autoopen_delay: String(pendingConfig.STYLE_autoopen_delay),
          buttoncolor1: pendingConfig.VISTA_buttoncolor1,
          buttoncolor2: pendingConfig.VISTA_buttoncolor2,
          position_marginX: String(pendingConfig.STYLE_position_marginX),
          position_marginY: String(pendingConfig.STYLE_position_marginY)
        }
      };

      // Crear una copia fresca de los temas para evitar referencias
      const temasPersonalizados = JSON.parse(JSON.stringify(configForApi.theme.themes));
      const temaIndex = pendingConfig.theme?.current ?? 0;

      // Asegurarse de que todos los temas tengan la propiedad inputFocusBorder
      temasPersonalizados.forEach(theme => {
        if (!('inputFocusBorder' in theme)) {
          theme.inputFocusBorder = theme.inputBorder ? theme.inputBorder : '#0056b3';
          console.log('Añadiendo propiedad inputFocusBorder al tema');
        }
      });

      // Aplicar colores personalizados solo al tema actual
      Object.keys(pendingConfig).forEach(key => {
        if (key.startsWith('CUSTOM_')) {
          const colorKey = key.replace('CUSTOM_', '');
          const valorActual = pendingConfig[key];

          // Actualizar el color en el tema actual
          if (temasPersonalizados[temaIndex]) {
            // Actualizar el color incluso si no existía previamente
            temasPersonalizados[temaIndex][colorKey] = valorActual;
            console.log(`Actualizando ${colorKey} a ${valorActual}`);
          }
        }
      });

      // Asignar los temas actualizados de vuelta a configForApi
      configForApi.theme.themes = temasPersonalizados;

      // Los temas ya están actualizados en configForApi.theme.themes

      // Añadir frases predefinidas solo si están habilitadas
      if (pendingConfig.FRASES_enabled) {
        configForApi.frases = {};
        for (let i = 1; i <= 5; i++) {
          const fraseKey = `FRASES_${i}`;
          if (pendingConfig[fraseKey]) {
            configForApi.frases[i] = pendingConfig[fraseKey];
          }
        }
      }


      // Configuración de Powered By (siempre se envía, con enabled true/false)
      configForApi.powered_by = {
        enabled: !!pendingConfig.POWERED_BY_enabled,
        label: pendingConfig.POWERED_BY_label || defaultConfig.POWERED_BY_label,
        // Para compatibilidad, si no hay linkText usa el antiguo POWERED_BY_linkText por defecto
        linkText: pendingConfig.POWERED_BY_linkText || defaultConfig.POWERED_BY_linkText,
        url: pendingConfig.POWERED_BY_url || defaultConfig.POWERED_BY_url,
        linkColor: pendingConfig.POWERED_BY_linkColor || defaultConfig.POWERED_BY_linkColor,
        labelColor: pendingConfig.POWERED_BY_labelColor || defaultConfig.POWERED_BY_labelColor
      };

      await saveChatbotConfig(selectedChatbot, configForApi);
      setFullConfig(configForApi); // Actualizar el objeto original con la versión guardada

      // Actualizar el pendingConfig con los temas modificados para asegurar que se guarden correctamente
      const updatedPendingConfig = { ...pendingConfig };
      if (!updatedPendingConfig.theme) {
        updatedPendingConfig.theme = {};
      }
      updatedPendingConfig.theme.themes = temasPersonalizados;

      // Crear una copia profunda del pendingConfig actualizado para evitar referencias
      const deepCopiedConfig = JSON.parse(JSON.stringify(updatedPendingConfig));

      // Guardar la configuración actualizada
      setPendingConfig(updatedPendingConfig);
      setSavedConfig(deepCopiedConfig);
      setHasChanges(false);

      // Forzar la recarga del iframe actualizando la key
      setIframeKey(Date.now());

      // Mostrar mensaje de éxito
      setSnackbarMessage('Cambios aplicados con éxito');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error al guardar los cambios:', error);

      // Mostrar mensaje de error
      setSnackbarMessage('Error al guardar los cambios');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!savedConfig) {
      setSavedConfig(pendingConfig);
      return;
    }

    if (iframeRef.current && selectedChatbot) {
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

      // Crear el objeto de configuración que espera el chatbot
      const chatbotConfig = {
        welcome: savedConfig.PRIMER_MENSAJE || '',
        title_icon: savedConfig.TITULO_ICO === 'custom'
          ? (savedConfig.TITULO_ICO_URL || '')
          : `https://ai4logic.com/${savedConfig.TITULO_ICO || 'logo1'}.png`,
        title_name: savedConfig.TITULO_NOM || '',
        modo: savedConfig.VISTA_modo || 'claro',
        styles: {
          position_marginX: String(savedConfig.STYLE_position_marginX || 20),
          position_marginY: String(savedConfig.STYLE_position_marginY || 20),
          openanimation: String(savedConfig.STYLE_openanimation || false),
          firstrunopen: String(savedConfig.STYLE_firstrunopen || false),
          autoopen_delay: String(savedConfig.STYLE_autoopen_delay || "0"),
          buttoncolor1: savedConfig.VISTA_buttoncolor1 || '#ff0080',
          buttoncolor2: savedConfig.VISTA_buttoncolor2 || '#ff8c00',
          animation: String(savedConfig.STYLE_animation || false),
          sizebutton: String(savedConfig.STYLE_sizebutton || 60)
        },
        commands: {
          reset_cookies: true
        },
        frases: savedConfig.FRASES_enabled ? {
          1: savedConfig.FRASES_1 || "",
          2: savedConfig.FRASES_2 || "",
          3: savedConfig.FRASES_3 || "",
          4: savedConfig.FRASES_4 || "",
          5: savedConfig.FRASES_5 || ""
        } : {},
        powered_by: savedConfig.POWERED_BY_enabled ? {
          enabled: true,
          label: savedConfig.POWERED_BY_label || defaultConfig.POWERED_BY_label,
          linkText: savedConfig.POWERED_BY_linkText || defaultConfig.POWERED_BY_linkText,
          url: savedConfig.POWERED_BY_url || defaultConfig.POWERED_BY_url,
          linkColor: savedConfig.POWERED_BY_linkColor || defaultConfig.POWERED_BY_linkColor,
          labelColor: savedConfig.POWERED_BY_labelColor || defaultConfig.POWERED_BY_labelColor
        } : undefined,
        // Aseguramos que los cambios de tema se reflejen correctamente
        theme: savedConfig.theme ? {
          current: savedConfig.theme.current || 0,
          // Usar directamente los temas guardados para asegurar que los cambios se reflejen
          themes: savedConfig.theme.themes || defaultThemes
        } : {
          current: 0,
          themes: defaultThemes
        }
      };

      // Añadir colores personalizados si existen en el savedConfig
      const colorespersonalizados = {};
      let hasCustomColors = false;

      // Si hay un tema seleccionado, usar sus colores
      if (savedConfig.theme && savedConfig.theme.themes && savedConfig.theme.themes[savedConfig.theme.current]) {
        const currentTheme = savedConfig.theme.themes[savedConfig.theme.current];
        Object.entries(currentTheme).forEach(([key, value]) => {
          colorespersonalizados[key] = value;
          hasCustomColors = true;
        });
      }

      // También incluir cualquier color personalizado adicional
      Object.keys(savedConfig).forEach(key => {
        if (key.startsWith('CUSTOM_')) {
          const colorKey = key.replace('CUSTOM_', '');
          colorespersonalizados[colorKey] = savedConfig[key];
          hasCustomColors = true;
        }
      });

      // Solo añadir los colores personalizados si realmente existen
      if (hasCustomColors) {
        chatbotConfig.colorespersonalizados = colorespersonalizados;
      }

      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              body { margin: 0; padding: 0; height: 100vh; overflow: hidden; background-color: #0b151b; }
              #chatbot { height: 100%; width: 100%; }
            </style>
          </head>
          <body>
            <div id="chatbot"></div>
            <script>
              var CLIENTE = "${selectedChatbot}";
              var RUTA = "https://ai4logic.com";
              
              // Cargar el script de manera controlada
              (function() {
                var script = document.createElement('script');
                script.src = RUTA + '/chatbot-widget2.js';
                script.async = true;
                script.onload = function() {
                  // Configuración directa sin hacer fetch
                  const data = ${JSON.stringify(chatbotConfig)};
                  // Crear una nueva instancia del chatbot con la configuración
                  new Chatbot(document.getElementById('chatbot'), CLIENTE, data.welcome, data, true);
                };
                document.head.appendChild(script);
              })();
            </script>
          </body>
        </html>
      `);
      iframeDoc.close();
    }
  }, [selectedChatbot, savedConfig]);

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': { borderColor: theme.palette.divider },
      '&:hover fieldset': { borderColor: theme.palette.primary.light },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputLabel-root': { color: theme.palette.text.secondary }
  };

  const renderSection = (title, children) => (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          mb: 1,
          p: 1.5,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.action.hover,
          borderRadius: '0 4px 4px 0'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              width: '8px',
              height: '8px',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '50%',
              marginRight: '12px'
            }
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2 }}>
        {children}
      </Box>
    </Box>
  );

  const [showNewPhraseInput, setShowNewPhraseInput] = useState(false);
  const [newPhrase, setNewPhrase] = useState('');

  return (
    <Container maxWidth={false}>
      <Box sx={{ p: 0, position: 'relative', minHeight: '100vh' }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Parámetros
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Configura los parámetros del sistema y observa los cambios en tiempo real.
          </Typography>
        </Paper>

        <Grid container spacing={2} sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}>
          {/* Columna izquierda - Parámetros */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                p: 2,
                height: { xs: '60vh', md: '80vh' },
                overflowY: 'auto',
                position: 'relative'
              }}
            >
              {/* Información General */}
              {renderSection('Información General',
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontWeight: 600
                        }}>Nombre del Asistente</Typography>
                        <TextField
                          value={pendingConfig.TITULO_NOM}
                          onChange={(e) => handleChange('TITULO_NOM', e.target.value)}
                          fullWidth
                          sx={textFieldStyle}
                          disabled={isReadOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontWeight: 600
                        }}>Icono del Chatbot</Typography>
                        <Button
                          onClick={(e) => setIconMenuAnchor(e.currentTarget)}
                          sx={{
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                            minWidth: '200px',
                            justifyContent: 'flex-start',
                            gap: 2,
                            px: 2,
                            height: '56px',
                            width: '100%'
                          }}
                          disabled={isReadOnly}
                        >
                          <Box
                            component="img"
                            src={pendingConfig.TITULO_ICO === 'custom'
                              ? pendingConfig.TITULO_ICO_URL
                              : `https://ai4logic.com/${pendingConfig.TITULO_ICO}.png`}
                            alt="Icono seleccionado"
                            sx={{
                              width: '32px',
                              height: '32px',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              e.target.src = 'https://ai4logic.com/logo1.png';
                            }}
                          />
                          {pendingConfig.TITULO_ICO === 'custom' ? 'URL Personalizada' :
                            pendingConfig.TITULO_ICO === 'logo1' ? 'Default' :
                              pendingConfig.TITULO_ICO === 'logoia1' ? 'Asistente' : 'Robot'}
                        </Button>
                        <Popover
                          open={Boolean(iconMenuAnchor)}
                          anchorEl={iconMenuAnchor}
                          onClose={() => setIconMenuAnchor(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            sx: {
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              mt: 1
                            }
                          }}
                        >
                          <Box sx={{ p: 1, width: '250px' }}>
                            <Stack spacing={1}>
                              {[
                                { value: 'logo1', label: 'Default', description: 'Icono predeterminado' },
                                { value: 'logoia1', label: 'Asistente', description: 'Icono de asistente IA' },
                                { value: 'logoia2', label: 'Robot', description: 'Icono de robot IA' }
                              ].map((icon) => (
                                <Paper
                                  key={icon.value}
                                  onClick={() => {
                                    handleChange('TITULO_ICO', icon.value);
                                    handleChange('TITULO_ICO_URL', '');
                                    setIconMenuAnchor(null);
                                  }}
                                  sx={{
                                    p: 1.5,
                                    cursor: 'pointer',
                                    backgroundColor: pendingConfig.TITULO_ICO === icon.value ? theme.palette.action.selected : theme.palette.background.paper,
                                    border: '1px solid',
                                    borderColor: pendingConfig.TITULO_ICO === icon.value ? theme.palette.primary.main : theme.palette.divider,
                                    '&:hover': {
                                      backgroundColor: theme.palette.action.hover,
                                      borderColor: theme.palette.primary.main,
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={`https://ai4logic.com/${icon.value}.png`}
                                    alt={icon.label}
                                    sx={{
                                      width: '40px',
                                      height: '40px',
                                      objectFit: 'contain'
                                    }}
                                  />
                                  <Box>
                                    <Typography sx={{ color: theme.palette.text.primary, fontSize: '0.875rem', fontWeight: 500 }}>
                                      {icon.label}
                                    </Typography>
                                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                      {icon.description}
                                    </Typography>
                                  </Box>
                                </Paper>
                              ))}
                              <Paper
                                onClick={() => {
                                  if (pendingConfig.TITULO_ICO !== 'custom') {
                                    handleChange('TITULO_ICO', 'custom');
                                  }
                                }}
                                sx={{
                                  p: 1.5,
                                  cursor: 'pointer',
                                  backgroundColor: pendingConfig.TITULO_ICO === 'custom' ? theme.palette.action.selected : theme.palette.background.paper,
                                  border: '1px solid',
                                  borderColor: pendingConfig.TITULO_ICO === 'custom' ? theme.palette.primary.main : theme.palette.divider,
                                  '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor: theme.palette.primary.main,
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: pendingConfig.TITULO_ICO === 'custom' ? 1 : 0 }}>
                                  <Box
                                    sx={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: theme.palette.primary.main,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: theme.palette.primary.contrastText,
                                      fontSize: '24px'
                                    }}
                                  >
                                    +
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: theme.palette.text.primary, fontSize: '0.875rem', fontWeight: 500 }}>
                                      URL Personalizada
                                    </Typography>
                                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                      Usar un icono personalizado
                                    </Typography>
                                  </Box>
                                </Box>
                                {pendingConfig.TITULO_ICO === 'custom' && (
                                  <TextField
                                    size="small"
                                    value={pendingConfig.TITULO_ICO_URL}
                                    onChange={(e) => handleChange('TITULO_ICO_URL', e.target.value)}
                                    placeholder="https://ejemplo.com/imagen.png"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    sx={{
                                      mt: 1,
                                      background: theme.palette.action.hover,
                                      borderRadius: 2,
                                      '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        borderRadius: 2,
                                        background: theme.palette.background.paper,
                                        transition: 'border-color 0.3s, box-shadow 0.3s',
                                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                        fontSize: '1rem',
                                        lineHeight: 1.7,
                                        padding: '10px 14px',
                                        '& fieldset': {
                                          borderColor: theme.palette.divider,
                                          borderWidth: 2
                                        },
                                        '&:hover fieldset': {
                                          borderColor: theme.palette.primary.light,
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: theme.palette.primary.main,
                                        }
                                      },
                                      '& textarea': {
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        background: 'transparent',
                                        color: theme.palette.text.primary,
                                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                        fontSize: '1rem',
                                        letterSpacing: '0.01em',
                                        '::placeholder': {
                                          color: theme.palette.text.secondary,
                                          opacity: 1,
                                          fontStyle: 'italic'
                                        }
                                      },
                                      '& .MuiInputBase-input::placeholder': {
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                        fontStyle: 'italic'
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </Paper>
                            </Stack>
                          </Box>
                        </Popover>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontWeight: 600
                        }}>Primer Mensaje</Typography>
                        <TextField
                          value={pendingConfig.PRIMER_MENSAJE}
                          onChange={(e) => handleChange('PRIMER_MENSAJE', e.target.value)}
                          multiline
                          rows={2}
                          fullWidth
                          sx={textFieldStyle}
                          disabled={isReadOnly}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                </Grid>
              )}

              {renderSection('Datos de Contacto',
                <>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600
                      }}>Email de Contacto</Typography>
                      <TextField
                        value={pendingConfig.CLIENTE_EMAIL}
                        onChange={(e) => handleChange('CLIENTE_EMAIL', e.target.value)}
                        fullWidth
                        sx={textFieldStyle}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600
                      }}>Nombre de Contacto</Typography>
                      <TextField
                        value={pendingConfig.CLIENTE_EMAIL_NOM}
                        onChange={(e) => handleChange('CLIENTE_EMAIL_NOM', e.target.value)}
                        fullWidth
                        sx={textFieldStyle}
                        disabled={isReadOnly}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{
                      color: theme.palette.text.secondary,
                      mb: 1,
                      mt: 1,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      fontWeight: 600
                    }}>Sitio Web</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={pendingConfig.CLIENTE_WEB}
                        onChange={(e) => handleChange('CLIENTE_WEB', e.target.value)}
                        fullWidth
                        sx={textFieldStyle}
                        disabled={isReadOnly}
                        placeholder="https://tusitio.com"
                      />
                      <Tooltip title="Visitar sitio web">
                        <span>
                          <IconButton
                            sx={{
                              color: theme.palette.primary.main,
                              backgroundColor: theme.palette.action.hover,
                              border: `1px solid ${theme.palette.divider}`,
                              ml: 1,
                              '&:hover': {
                                backgroundColor: theme.palette.action.selected
                              },
                              borderRadius: 2,
                              p: '8px',
                            }}
                            href={/^https?:\/\//.test(pendingConfig.CLIENTE_WEB) ? pendingConfig.CLIENTE_WEB : `https://${pendingConfig.CLIENTE_WEB}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={isReadOnly || !pendingConfig.CLIENTE_WEB || !/^([a-zA-Z]+:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- .\/\?%&=]*)?$/.test(pendingConfig.CLIENTE_WEB)}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </>
              )}

              {/* Powered By (solo si el admin lo permite en AdminOptions mediante CUSTOM_POWERED_BY) */}
              {hasCustomPoweredBy && renderSection('Powered by del chatbot [*]',
                <>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pendingConfig.POWERED_BY_enabled}
                            onChange={(e) => handleChange('POWERED_BY_enabled', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        }
                        label={
                          <Typography sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                            Mostrar "Powered by" en el pie del chatbot
                          </Typography>
                        }
                      />
                    </Grid>

                    {pendingConfig.POWERED_BY_enabled && (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Texto 'Powered by'"
                            value={pendingConfig.POWERED_BY_label}
                            onChange={(e) => handleChange('POWERED_BY_label', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={textFieldStyle}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Texto del enlace"
                            value={pendingConfig.POWERED_BY_linkText}
                            onChange={(e) => handleChange('POWERED_BY_linkText', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={textFieldStyle}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="URL destino"
                            value={pendingConfig.POWERED_BY_url}
                            onChange={(e) => handleChange('POWERED_BY_url', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={textFieldStyle}
                            disabled={isReadOnly}
                            placeholder="https://tuweb.com"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <ColorPickerField
                            label="Color del enlace"
                            value={pendingConfig.POWERED_BY_linkColor}
                            onChange={(color) => handleChange('POWERED_BY_linkColor', color)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <ColorPickerField
                            label="Color texto 'Powered by'"
                            value={pendingConfig.POWERED_BY_labelColor}
                            onChange={(color) => handleChange('POWERED_BY_labelColor', color)}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </>
              )}

              {/* Estilos y Animaciones */}
              {renderSection('Estilos y Animaciones',
                <>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600
                      }}>Tamaño del botón</Typography>
                      <Slider
                        value={pendingConfig.STYLE_sizebutton}
                        onChange={(e, value) => handleChange('STYLE_sizebutton', value)}
                        min={40}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        sx={{
                          color: theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.primary.contrastText,
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.palette.divider,
                          }
                        }}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600
                      }}>Margen Horizontal (X)</Typography>
                      <Slider
                        value={pendingConfig.STYLE_position_marginX}
                        onChange={(e, value) => handleChange('STYLE_position_marginX', value)}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelDisplay="auto"
                        sx={{
                          color: theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.primary.contrastText,
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.palette.divider,
                          }
                        }}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600
                      }}>Margen Vertical (Y)</Typography>
                      <Slider
                        value={pendingConfig.STYLE_position_marginY}
                        onChange={(e, value) => handleChange('STYLE_position_marginY', value)}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelDisplay="auto"
                        sx={{
                          color: theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.primary.contrastText,
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.palette.divider,
                          }
                        }}
                        disabled={isReadOnly}
                      />
                    </Grid>

                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pendingConfig.STYLE_animation}
                            onChange={(e) => handleChange('STYLE_animation', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        }
                        label={<Typography sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Animación de palpitar</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pendingConfig.STYLE_openanimation}
                            onChange={(e) => handleChange('STYLE_openanimation', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        }
                        label={<Typography sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Animación de persiana</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pendingConfig.STYLE_firstrunopen}
                            onChange={(e) => handleChange('STYLE_firstrunopen', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        }
                        label={<Typography sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Abrir en primera visita (desktop) [*]</Typography>}
                      />
                      {pendingConfig.STYLE_firstrunopen && (
                        <Box sx={{ ml: 4, mt: 1 }}>
                          <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem', mb: 1, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                            Tiempo de retraso: [*]
                          </Typography>
                          <Select
                            value={pendingConfig.STYLE_autoopen_delay || "0"}
                            onChange={(e) => handleChange('STYLE_autoopen_delay', e.target.value)}
                            displayEmpty
                            size="small"
                            sx={{
                              width: '120px',
                              '& .MuiOutlinedInput-root': {
                                color: theme.palette.text.primary,
                                '& fieldset': { borderColor: theme.palette.divider },
                                '&:hover fieldset': { borderColor: theme.palette.primary.light },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                              },
                              '& .MuiSelect-select': {
                                py: 1,
                                backgroundColor: theme.palette.background.paper
                              }
                            }}
                            disabled={isReadOnly}
                          >
                            <MenuItem value="0">0s</MenuItem>
                            <MenuItem value="1">1s</MenuItem>
                            <MenuItem value="2">2s</MenuItem>
                            <MenuItem value="3">3s</MenuItem>
                            <MenuItem value="4">4s</MenuItem>
                            <MenuItem value="5">5s</MenuItem>
                            <MenuItem value="10">10s</MenuItem>
                            <MenuItem value="20">20s</MenuItem>
                            <MenuItem value="30">30s</MenuItem>
                          </Select>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
              {/* Vista y Colores */}
              {renderSection('Vista y Colores',
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} container spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                          textAlign: { xs: 'center', md: 'left' }
                        }}>
                          Tema [*]
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{
                          color: theme.palette.primary.main,
                          mb: 0.5,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                          textAlign: { xs: 'center', md: 'left' }
                        }}>
                          Colores del Botón
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'center', md: 'flex-start' },
                        gap: 1,
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: theme.palette.background.paper,
                          p: 1,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          width: 'fit-content'
                        }}>
                          <Tooltip title="Modo Claro">
                            <LightModeIcon sx={{
                              color: (pendingConfig.theme?.current === 0) ? theme.palette.primary.main : theme.palette.text.secondary,
                              transition: 'color 0.3s ease',
                              fontSize: '20px'
                            }} />
                          </Tooltip>
                          <Slider
                            value={(pendingConfig.theme?.current === 0) ? 0 : 100}
                            onChange={(e, value) => {
                              const newThemeIndex = value === 0 ? 0 : 1;

                              // Crear nueva configuración con el nuevo índice de tema
                              const newConfig = { ...pendingConfig };

                              // Guardar los colores personalizados actuales en el tema actual antes de cambiar
                              const currentThemes = JSON.parse(JSON.stringify(pendingConfig.theme?.themes || defaultThemes));
                              const currentThemeIndex = pendingConfig.theme?.current ?? 0;

                              // Guardar los colores personalizados actuales en el tema actual
                              Object.keys(pendingConfig).forEach(key => {
                                if (key.startsWith('CUSTOM_')) {
                                  const colorKey = key.replace('CUSTOM_', '');
                                  if (currentThemes[currentThemeIndex][colorKey]) {
                                    currentThemes[currentThemeIndex][colorKey] = pendingConfig[key];
                                  }
                                }
                              });

                              // Actualizar el índice de tema
                              newConfig.theme = {
                                current: newThemeIndex,
                                themes: currentThemes
                              };

                              // Eliminar los colores personalizados actuales
                              Object.keys(newConfig).forEach(key => {
                                if (key.startsWith('CUSTOM_')) {
                                  delete newConfig[key];
                                }
                              });

                              // Aplicar los colores del nuevo tema seleccionado
                              Object.entries(currentThemes[newThemeIndex]).forEach(([key, value]) => {
                                newConfig[`CUSTOM_${key}`] = value;
                              });

                              // Actualizar el color del label de Powered By según el nuevo modo
                              newConfig.POWERED_BY_labelColor = getDefaultLabelColor(newThemeIndex === 1 ? 'oscuro' : 'claro');

                              setPendingConfig(newConfig);
                              setHasChanges(true);
                            }}
                            step={100}
                            marks
                            min={0}
                            max={100}
                            sx={{
                              color: theme.palette.primary.main,
                              width: '80px',
                              '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                                backgroundColor: theme.palette.primary.contrastText,
                                '&:hover': {
                                  boxShadow: 'none'
                                }
                              },
                              '& .MuiSlider-rail': {
                                backgroundColor: theme.palette.divider,
                                opacity: 0.5
                              },
                              '& .MuiSlider-mark': {
                                display: 'none'
                              },
                              '& .MuiSlider-track': {
                                height: 4
                              },
                              '& .MuiSlider-rail': {
                                height: 4
                              }
                            }}
                            disabled={isReadOnly}
                          />
                          <Tooltip title="Modo Oscuro">
                            <DarkModeIcon sx={{
                              color: (pendingConfig.theme?.current === 1) ? theme.palette.primary.main : theme.palette.text.secondary,
                              transition: 'color 0.3s ease',
                              fontSize: '20px'
                            }} />
                          </Tooltip>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            // Si se está abriendo el panel, aplicar los colores del tema actual
                            if (!colorCustomizationOpen) {
                              const temaIndex = pendingConfig.theme?.current ?? 0;
                              // Hacer una copia profunda para evitar modificar el original
                              const currentThemes = JSON.parse(JSON.stringify(pendingConfig.theme?.themes || defaultThemes));
                              const currentTheme = currentThemes[temaIndex];
                              const newConfig = { ...pendingConfig };

                              // Guardar los colores personalizados actuales en todos los temas
                              // Esto asegura que no se pierdan personalizaciones al cambiar de tema
                              for (let i = 0; i < currentThemes.length; i++) {
                                if (i !== temaIndex) { // No actualizar el tema actual, lo haremos después
                                  Object.keys(pendingConfig).forEach(key => {
                                    if (key.startsWith('CUSTOM_')) {
                                      const colorKey = key.replace('CUSTOM_', '');
                                      // Solo actualizar si el color existe en el tema
                                      if (currentThemes[i][colorKey] && pendingConfig.theme?.themes?.[i]?.[colorKey]) {
                                        currentThemes[i][colorKey] = pendingConfig.theme.themes[i][colorKey];
                                      }
                                    }
                                  });
                                }
                              }

                              // Actualizar el objeto tema con los temas actualizados
                              newConfig.theme = {
                                current: temaIndex,
                                themes: currentThemes
                              };

                              // Eliminar todos los colores personalizados
                              Object.keys(newConfig).forEach(key => {
                                if (key.startsWith('CUSTOM_')) {
                                  delete newConfig[key];
                                }
                              });

                              // Aplicar los colores del tema actual
                              Object.entries(currentTheme).forEach(([key, value]) => {
                                newConfig[`CUSTOM_${key}`] = value;
                              });

                              setPendingConfig(newConfig);
                            }

                            setColorCustomizationOpen(prev => !prev);
                          }}
                          startIcon={<ColorLensIcon />}
                          sx={{
                            mt: 1,
                            color: theme.palette.text.secondary,
                            borderColor: theme.palette.divider,
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: theme.palette.action.hover
                            }
                          }}
                          disabled={isReadOnly}
                        >
                          Personalizar tema
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={2} sx={{
                        justifyContent: { xs: 'center', md: 'flex-start' }
                      }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{
                            color: theme.palette.primary.main,
                            mb: 0.5,
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            textAlign: 'center'
                          }}>Color Superior</Typography>
                          <Button
                            onClick={(e) => setColorAnchor1(e.currentTarget)}
                            sx={{
                              minWidth: 'auto',
                              p: 0,
                              border: `2px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              overflow: 'hidden',
                              display: 'inline-flex',
                              '&:hover': {
                                border: `2px solid ${theme.palette.primary.main}`,
                              }
                            }}
                            disabled={isReadOnly}
                          >
                            <Box
                              sx={{
                                backgroundColor: pendingConfig.VISTA_buttoncolor1,
                                width: '36px',
                                height: '36px',
                              }}
                            />
                          </Button>
                          <Popover
                            open={Boolean(colorAnchor1)}
                            anchorEl={colorAnchor1}
                            onClose={() => setColorAnchor1(null)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                          >
                            <Box sx={{ p: 2 }}>
                              <ChromePicker
                                color={pendingConfig.VISTA_buttoncolor1}
                                onChange={(color) => handleChange('VISTA_buttoncolor1', color.hex)}
                              />
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => setColorAnchor1(null)}
                                sx={{
                                  mt: 2,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    borderColor: theme.palette.primary.dark,
                                  },
                                }}
                              >
                                Seleccionar
                              </Button>
                            </Box>
                          </Popover>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{
                            color: theme.palette.primary.main,
                            mb: 0.5,
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            textAlign: 'center'
                          }}>Color Inferior</Typography>
                          <Button
                            onClick={(e) => setColorAnchor2(e.currentTarget)}
                            sx={{
                              minWidth: 'auto',
                              p: 0,
                              border: `2px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              overflow: 'hidden',
                              display: 'inline-flex',
                              '&:hover': {
                                border: `2px solid ${theme.palette.primary.main}`,
                              }
                            }}
                            disabled={isReadOnly}
                          >
                            <Box
                              sx={{
                                backgroundColor: pendingConfig.VISTA_buttoncolor2,
                                width: '36px',
                                height: '36px',
                              }}
                            />
                          </Button>
                          <Popover
                            open={Boolean(colorAnchor2)}
                            anchorEl={colorAnchor2}
                            onClose={() => setColorAnchor2(null)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                          >
                            <Box sx={{ p: 2 }}>
                              <ChromePicker
                                color={pendingConfig.VISTA_buttoncolor2}
                                onChange={(color) => handleChange('VISTA_buttoncolor2', color.hex)}
                              />
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => setColorAnchor2(null)}
                                sx={{
                                  mt: 2,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    borderColor: theme.palette.primary.dark,
                                  },
                                }}
                              >
                                Seleccionar
                              </Button>
                            </Box>
                          </Popover>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {colorCustomizationOpen && (
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                      <Grid item xs={12}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 3,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            width: '100%',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)'
                          }}
                        >
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            paddingBottom: 1,
                            mb: 3
                          }}>
                            <Typography
                              sx={{
                                color: theme.palette.primary.main,
                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                fontWeight: 600,
                                fontSize: '1.5rem',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              Personalización de Colores
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                // Restablecer los colores al tema predeterminado actual
                                const temaIndex = pendingConfig.theme?.current ?? 0;
                                const newConfig = { ...pendingConfig };

                                // Eliminar todos los colores personalizados
                                Object.keys(newConfig).forEach(key => {
                                  if (key.startsWith('CUSTOM_')) {
                                    delete newConfig[key];
                                  }
                                });

                                // Aplicar los colores del tema predeterminado
                                Object.entries(defaultThemes[temaIndex]).forEach(([key, value]) => {
                                  newConfig[`CUSTOM_${key}`] = value;
                                });

                                // Actualizar la configuración
                                setPendingConfig(newConfig);
                                setHasChanges(true);
                              }}
                              startIcon={<RestartAltIcon />}
                              sx={{
                                color: theme.palette.text.secondary,
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              Restablecer
                            </Button>
                          </Box>
                          <Grid container spacing={3}>
                            {/* Colores del Chat */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Colores del Chat
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Fondo del Chat"
                                    value={pendingConfig.CUSTOM_chatBackground || defaultThemes[pendingConfig.theme?.current ?? 0].chatBackground}
                                    onChange={(color) => handleChange('CUSTOM_chatBackground', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Borde del Chat"
                                    value={pendingConfig.CUSTOM_chatBorder || defaultThemes[pendingConfig.theme?.current ?? 0].chatBorder}
                                    onChange={(color) => handleChange('CUSTOM_chatBorder', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Barra de Desplazamiento"
                                    value={pendingConfig.CUSTOM_scrollbarThumb || defaultThemes[pendingConfig.theme?.current ?? 0].scrollbarThumb}
                                    onChange={(color) => handleChange('CUSTOM_scrollbarThumb', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Fondo de la Barra"
                                    value={pendingConfig.CUSTOM_scrollbarTrack || defaultThemes[pendingConfig.theme?.current ?? 0].scrollbarTrack}
                                    onChange={(color) => handleChange('CUSTOM_scrollbarTrack', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Mensajes del Usuario */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Mensajes del Usuario
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color Principal"
                                    value={pendingConfig.CUSTOM_userMessageBg || defaultThemes[pendingConfig.theme?.current ?? 0].userMessageBg}
                                    onChange={(color) => handleChange('CUSTOM_userMessageBg', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color Secundario"
                                    value={pendingConfig.CUSTOM_userMessageBg2 || defaultThemes[pendingConfig.theme?.current ?? 0].userMessageBg2}
                                    onChange={(color) => handleChange('CUSTOM_userMessageBg2', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Texto del Mensaje"
                                    value={pendingConfig.CUSTOM_userMessageText || defaultThemes[pendingConfig.theme?.current ?? 0].userMessageText}
                                    onChange={(color) => handleChange('CUSTOM_userMessageText', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Mensajes del Asistente */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Mensajes del Asistente
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color Principal"
                                    value={pendingConfig.CUSTOM_assistantMessageBg || defaultThemes[pendingConfig.theme?.current ?? 0].assistantMessageBg}
                                    onChange={(color) => handleChange('CUSTOM_assistantMessageBg', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color Secundario"
                                    value={pendingConfig.CUSTOM_assistantMessageBg2 || defaultThemes[pendingConfig.theme?.current ?? 0].assistantMessageBg2}
                                    onChange={(color) => handleChange('CUSTOM_assistantMessageBg2', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Texto del Mensaje"
                                    value={pendingConfig.CUSTOM_assistantMessageText || defaultThemes[pendingConfig.theme?.current ?? 0].assistantMessageText}
                                    onChange={(color) => handleChange('CUSTOM_assistantMessageText', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Borde del Mensaje"
                                    value={pendingConfig.CUSTOM_assistantMessageBorder || defaultThemes[pendingConfig.theme?.current ?? 0].assistantMessageBorder}
                                    onChange={(color) => handleChange('CUSTOM_assistantMessageBorder', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Campo de Entrada */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Campo de Entrada
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Borde del Campo"
                                    value={pendingConfig.CUSTOM_inputBorder || defaultThemes[pendingConfig.theme?.current ?? 0].inputBorder}
                                    onChange={(color) => handleChange('CUSTOM_inputBorder', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Borde con Focus"
                                    value={pendingConfig.CUSTOM_inputFocusBorder || defaultThemes[pendingConfig.theme?.current ?? 0].inputFocusBorder}
                                    onChange={(color) => handleChange('CUSTOM_inputFocusBorder', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Fondo del Campo"
                                    value={pendingConfig.CUSTOM_inputBackground || defaultThemes[pendingConfig.theme?.current ?? 0].inputBackground}
                                    onChange={(color) => handleChange('CUSTOM_inputBackground', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Texto del Campo"
                                    value={pendingConfig.CUSTOM_inputText || defaultThemes[pendingConfig.theme?.current ?? 0].inputText}
                                    onChange={(color) => handleChange('CUSTOM_inputText', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color del Placeholder"
                                    value={pendingConfig.CUSTOM_placeholderColor || defaultThemes[pendingConfig.theme?.current ?? 0].placeholderColor}
                                    onChange={(color) => handleChange('CUSTOM_placeholderColor', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Encabezado */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Encabezado
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Fondo del Encabezado"
                                    value={pendingConfig.CUSTOM_headerBackground || defaultThemes[pendingConfig.theme?.current ?? 0].headerBackground}
                                    onChange={(color) => handleChange('CUSTOM_headerBackground', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Borde del Encabezado"
                                    value={pendingConfig.CUSTOM_headerBorder || defaultThemes[pendingConfig.theme?.current ?? 0].headerBorder}
                                    onChange={(color) => handleChange('CUSTOM_headerBorder', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Texto del Encabezado"
                                    value={pendingConfig.CUSTOM_headerText || defaultThemes[pendingConfig.theme?.current ?? 0].headerText}
                                    onChange={(color) => handleChange('CUSTOM_headerText', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Otros Elementos */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 2,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '8px 0',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                Otros Elementos
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color de Enlaces"
                                    value={pendingConfig.CUSTOM_linkColor || defaultThemes[pendingConfig.theme?.current ?? 0].linkColor}
                                    onChange={(color) => handleChange('CUSTOM_linkColor', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Color de Hover"
                                    value={pendingConfig.CUSTOM_linkHoverColor || defaultThemes[pendingConfig.theme?.current ?? 0].linkHoverColor}
                                    onChange={(color) => handleChange('CUSTOM_linkHoverColor', color)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                  <ColorPickerField
                                    label="Indicador de Estado"
                                    value={pendingConfig.CUSTOM_statusColor || defaultThemes[pendingConfig.theme?.current ?? 0].statusColor}
                                    onChange={(color) => handleChange('CUSTOM_statusColor', color)}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Botones de acción */}
                            <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 3 }}>
                              <Button
                                variant="outlined"
                                size="large"
                                startIcon={<DeleteIcon />}
                                onClick={() => {
                                  // Obtener el tema actual
                                  const temaIndex = pendingConfig.theme?.current ?? 0;
                                  const defaultThemeColors = defaultThemes[temaIndex];
                                  const newConfig = { ...pendingConfig };

                                  // Eliminar todos los colores personalizados
                                  Object.keys(newConfig).forEach(key => {
                                    if (key.startsWith('CUSTOM_')) {
                                      delete newConfig[key];
                                    }
                                  });

                                  // Aplicar los colores por defecto del tema actual
                                  Object.entries(defaultThemeColors).forEach(([key, value]) => {
                                    newConfig[`CUSTOM_${key}`] = value;
                                  });

                                  setPendingConfig(newConfig);
                                  setHasChanges(true);
                                }}
                                sx={{
                                  color: '#ff4444',
                                  borderColor: '#ff4444',
                                  borderWidth: '2px',
                                  padding: '10px 24px',
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: '#ff6666',
                                    backgroundColor: 'rgba(255, 68, 68, 0.12)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(255, 68, 68, 0.2)'
                                  }
                                }}
                              >
                                Restablecer tema
                              </Button>
                              <Button
                                variant="contained"
                                size="large"
                                onClick={() => setColorCustomizationOpen(false)}
                                sx={{
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  padding: '10px 32px',
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4]
                                  }
                                }}
                              >
                                Minimizar personalización
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}

              {/* Frases predefinidas */}
              {renderSection('Sugerencias Predefinidas [*]',
                <>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pendingConfig.FRASES_enabled}
                            onChange={(e) => handleChange('FRASES_enabled', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        }
                        label={<Typography sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Habilitar sugerencias predefinidas</Typography>}
                      />
                    </Grid>

                    {pendingConfig.FRASES_enabled && (
                      <Grid item xs={12}>
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mb: 2,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontStyle: 'italic',
                          fontSize: '0.85rem'
                        }}>
                          Añade hasta 5 sugerencias predefinidas que aparecerán como sugerencias para los usuarios (máximo 80 caracteres cada una)
                        </Typography>

                        {/* Estado local para la nueva frase */}
                        {/* Frases existentes */}
                        {[1, 2, 3, 4, 5].map(index => {
                          const fraseKey = `FRASES_${index}`;
                          if (!pendingConfig[fraseKey]) return null;

                          return (
                            <Box key={index} sx={{ mb: 2, position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <TextField
                                fullWidth
                                value={pendingConfig[fraseKey] || ''}
                                onChange={(e) => handleChange(fraseKey, e.target.value)}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 80 }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': { borderColor: theme.palette.divider },
                                    '&:hover fieldset': { borderColor: theme.palette.primary.light },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                                  }
                                }}
                                disabled={isReadOnly}
                              />
                              <IconButton
                                onClick={() => {
                                  const newConfig = { ...pendingConfig };
                                  // Eliminar la frase actual
                                  delete newConfig[fraseKey];
                                  // Reordenar las frases restantes
                                  for (let i = index; i < 5; i++) {
                                    const currentKey = `FRASES_${i + 1}`;
                                    const nextKey = `FRASES_${i}`;
                                    if (newConfig[currentKey]) {
                                      newConfig[nextKey] = newConfig[currentKey];
                                      delete newConfig[currentKey];
                                    }
                                  }
                                  setPendingConfig(newConfig);
                                  setHasChanges(true); // Marcar que hay cambios al eliminar
                                }}
                                sx={{
                                  ml: 1,
                                  color: theme.palette.text.secondary,
                                  '&:hover': { color: theme.palette.error.main }
                                }}
                                disabled={isReadOnly}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          );
                        })}

                        {/* Campo para nueva frase */}
                        {showNewPhraseInput && (
                          <Box sx={{ mb: 2, position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <TextField
                              fullWidth
                              value={newPhrase}
                              onChange={(e) => {
                                setNewPhrase(e.target.value);
                                setHasChanges(true); // Activar el botón de guardar mientras se escribe
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newPhrase.trim()) {
                                  // Encontrar el próximo índice disponible
                                  let nextIndex = 1;
                                  while (pendingConfig[`FRASES_${nextIndex}`] && nextIndex <= 5) {
                                    nextIndex++;
                                  }
                                  if (nextIndex <= 5) {
                                    const newConfig = {
                                      ...pendingConfig,
                                      [`FRASES_${nextIndex}`]: newPhrase.trim()
                                    };
                                    setPendingConfig(newConfig);
                                    setNewPhrase('');
                                    setShowNewPhraseInput(false);
                                  }
                                }
                              }}
                              onBlur={() => {
                                if (newPhrase.trim()) {
                                  // Encontrar el próximo índice disponible
                                  let nextIndex = 1;
                                  while (pendingConfig[`FRASES_${nextIndex}`] && nextIndex <= 5) {
                                    nextIndex++;
                                  }
                                  if (nextIndex <= 5) {
                                    const newConfig = {
                                      ...pendingConfig,
                                      [`FRASES_${nextIndex}`]: newPhrase.trim()
                                    };
                                    setPendingConfig(newConfig);
                                    setNewPhrase('');
                                    setShowNewPhraseInput(false);
                                  }
                                }
                              }}
                              variant="outlined"
                              size="small"
                              inputProps={{ maxLength: 80 }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  color: theme.palette.text.primary,
                                  '& fieldset': { borderColor: theme.palette.divider },
                                  '&:hover fieldset': { borderColor: theme.palette.primary.light },
                                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                                }
                              }}
                            />
                            <IconButton
                              onClick={() => {
                                setNewPhrase('');
                                setShowNewPhraseInput(false);
                              }}
                              sx={{
                                ml: 1,
                                color: theme.palette.text.secondary,
                                '&:hover': { color: theme.palette.error.main }
                              }}
                              disabled={isReadOnly}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}

                        {/* Botón para añadir nueva frase */}
                        {!showNewPhraseInput &&
                          (!pendingConfig.FRASES_1 ||
                            !pendingConfig.FRASES_2 ||
                            !pendingConfig.FRASES_3 ||
                            !pendingConfig.FRASES_4 ||
                            !pendingConfig.FRASES_5) && (
                            <Button
                              onClick={() => setShowNewPhraseInput(true)}
                              startIcon={<AddIcon />}
                              sx={{
                                mt: 2,
                                color: theme.palette.text.secondary,
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                              variant="outlined"
                              disabled={isReadOnly}
                            >
                              Añadir sugerencia
                            </Button>
                          )}
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Paper>
          </Grid>

          {/* Columna derecha - Chatbot */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                p: 2,
                height: { xs: '60vh', md: '80vh' },
                overflow: 'hidden',
                mb: { xs: 2, md: 0 }
              }}
            >
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Vista Previa del asistente
              </Typography>
              <Box sx={{
                height: 'calc(100% - 50px)',
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <iframe
                  key={iframeKey} // Key for forcing re-renders
                  ref={iframeRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: theme.palette.background.paper
                  }}
                  title="Asistente Preview"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Botón de guardar fijo */}
        {hasChanges && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000
            }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon sx={{ color: theme.palette.primary.contrastText }} />}
              onClick={handleSave}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
                height: '40px',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                width: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                  '& svg': {
                    fontSize: '1.2rem'
                  }
                }
              }}
            >
              Guardar Cambios
            </Button>
          </Box>
        )}

        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', position: 'flex' }}>
          [*] Esta opción solo está disponible en la versión Widget 2.0. Actualiza a la nueva versión desde el apartado de Implementación.
        </Typography>

        {/* Snackbar para mostrar mensajes */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              backgroundColor: snackbarSeverity === 'success' ? theme.palette.success.main : theme.palette.error.main,
              color: snackbarSeverity === 'success'
                ? theme.palette.getContrastText(theme.palette.success.main)
                : theme.palette.getContrastText(theme.palette.error.main),
              border: `1px solid ${snackbarSeverity === 'success' ? theme.palette.success.main : theme.palette.error.main}`,
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success'
                  ? theme.palette.getContrastText(theme.palette.success.main)
                  : theme.palette.getContrastText(theme.palette.error.main)
              }
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

// Componente para seleccionar colores
const ColorPickerField = ({ label, value, onChange }) => {
  const [colorAnchor, setColorAnchor] = useState(null);
  const theme = useTheme();

  return (
    <Box sx={{
      mb: 3,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.9rem',
          mb: 1,
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          fontWeight: 600,
          textAlign: 'center',
          width: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        width: '100%'
      }}>
        <Button
          onClick={(e) => setColorAnchor(e.currentTarget)}
          sx={{
            minWidth: 'auto',
            width: '60px',
            height: '60px',
            p: 0,
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            backgroundColor: value,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: value,
              border: `2px solid ${theme.palette.primary.main}`,
              transform: 'scale(1.05)',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
            }
          }}
        />
        <Box
          sx={{
            width: '100%',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            fontSize: '0.85rem',
            cursor: 'pointer',
            '&:hover': {
              borderColor: theme.palette.primary.main,
            }
          }}
        //onClick={(e) => setColorAnchor(e.currentTarget)}
        >
          {value}
        </Box>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2 }}>
            <ChromePicker
              color={value}
              onChange={(color) => onChange(color.hex)}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={() => setColorAnchor(null)}
              sx={{
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              Seleccionar
            </Button>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default Settings;
